// The module's strings, in English. Same key set as es.js — see its header.

export default {
  errors: {
    REPORT_SCHEDULE_OWNER_ONLY: 'A scheduled report runs with its owner’s permissions: only its owner can change it.',
  },
  // Como se llama este modulo en las pantallas de la plataforma (el
  // selector de la demo, Roles, Auditoria). Sin esto salen listando el
  // slug en ingles, que se lee como un modulo a medio hacer.
  modules: { reports: 'Reports' },

  reports: {
    assistant: {
      save: {
        action: 'Save a report',
        field: { title: 'Name', sections: 'Sections', period: 'Period' },
        after: 'It saves the questions: reopening it recomputes the figures',
        outcome: 'Report "{title}" saved',
      },
      schedule: {
        action: 'Send a report on a schedule',
        summary: {
          daily: 'Send "{title}" every day at {hour}:00',
          weekly: 'Send "{title}" every week (day {day}) at {hour}:00',
          monthly: 'Send "{title}" every month on day {day} at {hour}:00',
          quarterly: 'Send "{title}" every quarter on day {day} at {hour}:00',
        },
        frequency: { daily: 'Every day', weekly: 'Every week', monthly: 'Every month', quarterly: 'Every quarter' },
        field: {
          report: 'Report',
          frequency: 'How often',
          weekday: 'Weekday (1 = Monday)',
          monthDay: 'Day of the month',
          hour: 'Hour',
          recipients: 'To',
        },
        you: 'You',
        where: 'Report',
        notSaved: 'No saved report by that name: save it first, then schedule it',
        asOwner: 'It is computed with your permissions every time it goes out',
        outcome: '"{title}" scheduled',
      },
    },
    title: 'Reports',
    subtitle: 'The numbers of the whole house, and what they mean',
    guide: 'Each tab is an installed module that knows how to measure something. That module '
      + 'computes the figures, with your permissions: there is no copy of its data here.',

    period: 'Period',
    total: 'Total',

    nav: {
      saved: 'Saved',
      explore: 'Explore',
    },

    kind: {
      report: 'Report',
      board: 'Board',
    },

    tabs: {
      all: 'The whole company',
    },

    empty: {
      title: 'Nothing to measure yet',
      hint: 'Reports come from the installed modules. Install one — invoicing, projects, '
        + 'purchasing — and its tab appears on its own.',
    },

    actions: {
      save: 'Save',
      cancel: 'Cancel',
      edit: 'Edit',
      done: 'Done',
      remove: 'Remove',
    },

    fields: {
      title: 'Name',
      description: 'Description',
      modules: 'Where the figures come from',
      visibility: 'Who sees it',
      lastRun: 'Last opened',
    },

    severity: {
      critical: 'Critical',
      warning: 'Warning',
      info: 'Info',
      good: 'Good news',
    },

    summary: {
      title: 'Reading of the figures',
      subtitle: 'Written over the numbers below, and only over them',
      generate: 'Read the figures',
      again: 'Read them again',
      working: 'Reading the figures…',
      idleTitle: 'No reading yet',
      idleHint: 'It takes a few seconds and spends AI budget, so it is asked for.',
      quiet: 'Nothing worth reporting in this period.',
      caveat: 'This is a reading of the figures below, not another figure. The numbers are the '
        + 'report; this is an opinion of them.',
      evidence: 'What it rests on',
      impact: 'What is at stake',
      cause: 'Possible cause',
      action: 'What to do',
      confidence: {
        high: 'high confidence',
        medium: 'medium confidence',
        low: 'low confidence',
      },
      unavailable: {
        DISABLED: 'The automatic reading is switched off in the module settings.',
        NO_DATA: 'There are not enough figures in this period to say anything.',
        UNREADABLE: 'The answer could not be read. Try again.',
        AI_PROVIDER_ERROR: 'The AI provider did not answer. The figures below are fine.',
        generic: 'The reading is not available right now.',
      },
    },

    kpis: {
      title: 'The figures',
      subtitleAll: 'What every installed module leads with',
      subtitleModule: 'What {module} leads with',
      empty: 'No module returned figures for this period',
      failed: 'These could not be read: {modules}',
    },

    changes: {
      title: 'What has changed',
      subtitle: 'Ordered by what is at stake, not by the percentage',
      emptyTitle: 'Nothing moved enough',
      emptyHint: 'The threshold is set in the module configuration.',
    },

    insights: {
      title: 'What you should know',
      subtitle: 'What the modules raised without anybody asking',
      emptyTitle: 'Nothing to report',
      emptyHint: 'That is good news: nothing crossed the lines you drew.',
      why: 'Why?',
      acknowledge: 'I know',
    },

    focus: {
      subtitle: 'The same figure, month by month. Click a row to go one level down.',
      open: 'Open in Explore',
      close: 'Close',
    },

    chart: {
      empty: 'Nothing to draw',
    },

    widget: {
      noRows: 'No rows in this period',
      nothing: 'Not computed',
      failed: 'It could not be computed',
      truncated: 'The first {shown} rows are shown. The totals cover everything.',
    },

    quality: {
      generic: 'Note about the data: {code}',
      NO_DATA: 'There is no data in this period.',
      SHORT_HISTORY: 'Only {points} periods of history: too few to talk about a trend.',
      NOT_ENOUGH_FOR_FORECAST: 'With {points} periods nothing can be forecast meaningfully.',
      MOSTLY_EMPTY: '{empty} of {points} periods are empty.',
      INVOICES_WITHOUT_DUE_DATE: '{percent}% of invoices have no due date, so they cannot come '
        + 'out as overdue.',
      INVOICE_LINES_WITHOUT_COST: '{percent}% of invoices carry no cost on their lines: the '
        + 'margin reads better than it is.',
      QUOTES_LOST_WITHOUT_REASON: '{percent}% of lost quotes do not say why.',
      CUSTOMERS_WITHOUT_SECTOR: '{percent}% of customers have no sector.',
      PROJECTS_WITHOUT_BUDGET: '{percent}% of open jobs have no budgeted cost, so they cannot '
        + 'come out as overrunning.',
      PROJECTS_WITHOUT_COST: '{percent}% of open jobs have no cost posted against them.',
      ORDERS_WITHOUT_EXPECTED_DATE: '{percent}% of orders have no expected date.',
      MATERIALS_WITHOUT_COST: '{percent}% of the material in stock has no cost: the valuation '
        + 'falls short.',
      ORDERS_WITHOUT_ESTIMATE: '{percent}% of the finished orders had no estimated hours.',
      REFUELS_WITHOUT_PRICE: '{percent}% of refuellings carry no amount.',
      ATTENDANCE_DAYS_LEFT_OPEN: '{count} days ({percent}%) were never clocked out: their hours '
        + 'are counted up to right now.',
      ATTENDANCE_PERIOD_TOO_LARGE: 'The period holds more than {limit} entries and was cut. '
        + 'Ask for a shorter period.',
    },

    findings: {
      metric_moved: '"{metric}": {percent}% against the previous period.',
      module_insight: 'A module raised a finding.',
      invoices_overdue: '{count} invoices are overdue, the oldest by {days} days ({amount} €).',
      customer_concentration: '{name} is {percent}% of what was invoiced in the period.',
      customers_gone_quiet: '{count} active customers have not been invoiced for months. The '
        + 'largest is {name}, {months} months ago.',
      quotes_gone_quiet: '{count} quotes sent over a month ago are still unanswered ({amount} €).',
      projects_over_budget: '{count} open jobs are over budget. {code} is {percent}% above it.',
      supplier_concentration: '{name} is {percent}% of what was bought in the period.',
      supplier_delivering_late: '{name} is arriving {days} days late on average, over {orders} '
        + 'orders.',
      stock_not_moving: '{count} materials have not moved in a year, worth {amount} €.',
      product_over_estimate: '{name} is taking {percent}% longer than estimated, over {orders} '
        + 'orders.',
      field_incident_rate: '{rate} incidents per 100 work orders in the period.',
      maintenance_overdue: '{count} of {total} maintenance plans are overdue, the oldest by '
        + '{days} days.',
      asset_repeat_failures: '{name} has failed {count} times in the period, costing {hours} '
        + 'hours of downtime.',
      absences_awaiting_approval: '{count} absence requests are waiting for an answer, the '
        + 'oldest for {days} days.',
      personnel_documents_expired: '{count} staff documents are out of date.',
      personnel_documents_expiring: '{count} staff documents expire within a month, across '
        + '{people} people.',
      market_cost_exposure: '{name} moved {percent}%: an estimated {amount} € a year '
        + '({confidence}% confidence).',
      leads_unanswered: '{count} website enquiries are still unanswered, the oldest for {days} '
        + 'days.',
    },

    filters: {
      period: 'Period',
      from: 'From',
      to: 'To',
      compare: 'Compare with',
    },

    periods: {
      this_month: 'This month',
      last_month: 'Last month',
      this_quarter: 'This quarter',
      last_quarter: 'Last quarter',
      this_year: 'This year',
      last_year: 'Last year',
      year_to_date: 'Year to date',
      last_7_days: 'Last 7 days',
      last_30_days: 'Last 30 days',
      last_90_days: 'Last 90 days',
      last_6_months: 'Last 6 months',
      last_12_months: 'Last 12 months',
      all_time: 'All time',
      custom: 'Between two dates',
    },

    compare: {
      previous_period: 'The previous period',
      previous_year: 'The same period last year',
      none: 'Nothing',
    },

    visualisations: {
      kpi: 'One figure',
      line: 'Line',
      area: 'Area',
      bar: 'Bars',
      stacked_bar: 'Stacked bars',
      pie: 'Pie',
      donut: 'Donut',
      table: 'Table',
    },

    operators: {
      eq: 'is',
      ne: 'is not',
      in: 'is one of',
      nin: 'is none of',
      gt: 'greater than',
      gte: 'greater or equal',
      lt: 'less than',
      lte: 'less or equal',
      contains: 'contains',
      between: 'between',
      exists: 'has a value',
    },

    explore: {
      title: 'Explore',
      subtitle: 'One question at a time, about whatever you have installed',
      guide: 'Pick a subject, the figures you want and how to group them. Only what you may see '
        + 'is offered: what is not in the lists is not yours.',
      question: 'The question',
      dataset: 'About what',
      datasetHint: 'Every installed module contributes its own.',
      grain: 'One row is: {grain}',
      metrics: 'What to measure',
      metricsHint: 'Choose none and you get the ones the module leads with.',
      metricsPlaceholder: 'Type and press Enter',
      dimensions: 'Grouped by',
      dimensionsHint: 'Three at most. To narrow further, use a filter.',
      dimensionsPlaceholder: 'Customer, sector, salesperson…',
      granularity: 'Over time',
      noGranularity: 'No time axis',
      autoVisualisation: 'Whatever suits it',
      granularities: {
        auto: 'Automatic',
        day: 'By day',
        week: 'By week',
        month: 'By month',
        quarter: 'By quarter',
        year: 'By year',
      },
      dateField: 'Which date decides',
      dateFieldHint: 'It changes what "in July" means.',
      visualisation: 'How it is drawn',
      limit: 'How many rows',
      filters: 'Filters',
      filtersHint: 'The totals still cover everything that passes the filter.',
      addFilter: 'Add a filter',
      removeFilter: 'Remove',
      value: 'Value',
      run: 'Compute',
      save: 'Save as a report',
      saveTitle: 'Save the question',
      saved: 'Saved',
      answerBy: '{metrics} by {dimension}',
      rowCount: '{count} rows',
      reading: 'What shape it has',
      readingHint: 'Trend, odd values and forecast, each with how sure it is',
      readingIdle: 'Not analysed yet',
      analyse: 'Analyse',
      trend: 'Trend',
      trendUnclear: 'No clear trend with {points} periods.',
      trends: {
        up: 'Up {percent}% per period ({fit}% fit).',
        down: 'Down {percent}% per period ({fit}% fit).',
        flat: 'Flat.',
        unknown: 'Not enough data.',
      },
      anomalies: 'Odd periods',
      forecast: 'Forecast',
      forecastNone: 'Not enough history to forecast anything.',
      forecastNext: 'Next period: {value} (between {low} and {high}, {confidence}).',
      confidence: {
        high: 'high confidence',
        medium: 'medium confidence',
        low: 'low confidence',
        insufficient: 'no basis',
      },
      caveats: 'Read with care',
    },

    saved: {
      title: 'Saved reports',
      subtitle: 'The questions somebody wanted to ask again',
      search: 'Search',
      searchPlaceholder: 'By name, description or tag',
      module: 'From which module',
      allModules: 'All of them',
      empty: 'Nothing saved yet',
      never: 'Never',
      template: 'Template',
      company: 'The whole company',
      shared: 'Shared',
      private: 'Only me',
      scheduled: 'Scheduled',
      scopes: {
        all: 'All',
        mine: 'Mine',
        shared: 'Shared with me',
        templates: 'Templates',
        scheduled: 'Scheduled',
      },
    },

    report: {
      loading: 'Opening the report…',
      periodHint: 'Changing the period here does not change the report: it is a look, not a '
        + 'decision.',
      narrate: 'Write the conclusions',
      export: 'Export',
      schedule: 'Schedule',
      saved: 'Saved',
      editing: 'Add to the report',
      sectionTitle: 'Section title',
      addText: 'Some text',
      addNarrative: 'AI conclusions',
      addInsights: 'The findings',
      addQuery: 'A new question',
      conclusions: 'Conclusions',
      conclusionsHint: 'Written over the figures in this section',
      conclusionsIdle: 'Press "Write the conclusions" above',
      emptyTitle: 'The report is empty',
      emptyHint: 'Add a question from Explore, or a piece of text from here.',
      formats: {
        pdf: 'PDF',
        docx: 'Word',
        csv: 'CSV',
      },
    },

    schedule: {
      title: 'Schedule the report',
      hint: 'It goes out on its own, at the hour you say, by email to people who may have no '
        + 'account here. It is computed with YOUR permissions.',
      enabled: 'Send it on its own',
      frequency: 'How often',
      frequencies: {
        daily: 'Every day',
        weekly: 'Every week',
        monthly: 'Every month',
        quarterly: 'Every quarter',
      },
      weekday: 'Which day of the week (1 = Monday)',
      monthday: 'Which day of the month',
      hour: 'At what hour (UTC)',
      recipients: 'To which addresses',
      recipientsHint: 'People outside: the board, the accountant.',
      recipientsPlaceholder: 'mail{\'@\'}example.com',
      roles: 'And to whoever holds these roles',
      rolesHint: 'It keeps working when somebody leaves.',
      format: 'In what format',
      saved: 'Scheduled',
    },

    export: {
      index: 'Contents',
      generated: 'Generated',
    },

    entity: {
      subtitle: 'What every installed module knows about this record',
      emptyTitle: 'No module knows anything about this',
      emptyHint: 'Either no installed module measures it, or you may not see the ones that do.',
      failed: 'This module could not answer.',
      open: 'Open',
      footnote: 'Each block is computed by its own module, with your permissions. No figure is '
        + 'stored here.',
      kinds: {
        customer: 'Customer',
        supplier: 'Supplier',
        project: 'Job',
        user: 'Person',
        material: 'Material',
        asset: 'Machine',
        site: 'Site',
        team: 'Crew',
        vehicle: 'Vehicle',
      },
    },

    dashboard: {
      findings: 'Open findings',
      findingsHelp: 'How many critical or warning findings have not been marked as seen',
      findingsList: 'What you should know',
      findingsListHelp: 'The latest findings the modules raised on their own',
      mine: 'My reports',
      mineHelp: 'The reports you saved, most recently changed first',
    },

    related: {
      figures: 'Figures',
    },

    notifications: {
      categories: {
        insight: 'Findings from the analysis',
      },
      insights: {
        title: 'There is something to look at',
        body: 'Tonight\'s analysis raised {count} findings.',
      },
    },

    analytics: {
      usage: {
        label: 'Saved reports',
        help: 'One row per saved report, with how often it has been opened',
      },
      dates: {
        created: 'When it was saved',
      },
      metrics: {
        count: 'Reports',
        runs: 'Times opened',
      },
      dimensions: {
        owner: 'Who saved it',
        kind: 'Report or board',
      },
    },
  },

  permissions: {
    reports: {
      read: 'Open Reports and ask questions. It grants access to no figure: each number is '
        + 'computed by its own module with the permissions of whoever asked',
      write: 'Save reports and boards of your own',
      share: 'Hand a report to somebody else. Sharing never widens what they may see',
      schedule: 'Put a report on a clock so it goes out by email on its own',
      manage: 'Edit and delete anybody\'s reports, and publish templates',
    },
  },

  audit: {
    actions: {
      report: {
        created: 'Report created',
        updated: 'Report changed',
        deleted: 'Report deleted',
        exported: 'Report exported',
      },
    },
  },

  settings: {
    ns: {
      reports: {
        // El nombre de la sección en la pantalla de Ajustes.
        title: 'Reports',

        alertThresholdPct: {
          label: 'Tell me when a figure moves more than this',
          help: 'Against the previous period. Lower means more findings and more noise; a '
            + 'company with a quiet month at 5% will stop reading them.',
        },
        alertMinImpact: {
          label: 'And only when the change is worth at least this much',
          help: 'In euros, for the figures measured in euros. A 40% rise on 300 € is '
            + 'arithmetic, not news. 0 means every change past the percentage counts.',
        },
        alertPeriod: {
          label: 'The period the nightly analysis looks at',
          help: 'And it is compared against the one immediately before it.',
        },
        alertRoles: {
          label: 'Who is told when something important is found',
          help: 'Roles, not people, so it keeps working when somebody leaves. Only critical and '
            + 'warning findings; the rest wait on the screen.',
        },
        aiSummary: {
          label: 'Let the assistant write the reading of the figures',
          help: 'The block at the top of each tab, and the conclusions in an exported report. '
            + 'Off means the numbers and the findings, with nobody writing about them. It costs '
            + 'AI budget every time it runs.',
        },
        overviewDatasets: {
          label: 'How many subjects the summary tab covers',
          help: 'The whole-company tab asks every installed module for its leading figures. '
            + 'With fifteen modules that is a slow screen; each module still has its own tab.',
        },
        overviewCharts: {
          label: 'How many charts the summary tab draws',
          help: 'The figures come with their own history and drawing it costs nothing extra. What it '
            + 'does cost is the screen: past half a dozen, a summary stops being one. Zero leaves '
            + 'the tiles and the findings, and every chart one click away.',
        },
      },
    },

    mail: {
      reports__scheduled_report: {
        label: 'Scheduled reports',
        help: 'The mailbox a report that sends itself goes out from, with its PDF attached.',
      },
    },
  },

  assistant: {
    openers: {
      reports: {
        how_is_it_going: 'How is the company doing this month?',
        what_changed: 'What has changed since last month?',
        worry: 'What should I be worried about?',
      },
    },
  },
}
